import type { TooltipContentProps } from '@radix-ui/react-tooltip';
import { forwardRef, memo, type ComponentProps, type ReactElement, type ReactNode } from 'react';
import * as Tooltip from './Tooltip';
import { classNames } from '~/utils/classNames';

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

interface BaseLegacyIconButtonProps {
  size?: IconSize;
  className?: string;
  tabIndex?: number;
  iconClassName?: string;
  disabledClassName?: string;
  title?: string | ReactElement;
  side?: TooltipContentProps['side'];
  sideOffset?: TooltipContentProps['sideOffset'];
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  'aria-controls'?: ComponentProps<'button'>['aria-controls'];
  'aria-expanded'?: ComponentProps<'button'>['aria-expanded'];
  'aria-label'?: ComponentProps<'button'>['aria-label'];
}

type LegacyIconButtonWithoutChildrenProps = {
  icon: string;
  children?: undefined;
} & BaseLegacyIconButtonProps;

type LegacyIconButtonWithChildrenProps = {
  icon?: undefined;
  children: ReactNode;
} & BaseLegacyIconButtonProps;

type LegacyIconButtonProps = LegacyIconButtonWithoutChildrenProps | LegacyIconButtonWithChildrenProps;

/**
 * @deprecated Use ~/components/ui/IconButton instead
 */
export const LegacyIconButton = memo(
  forwardRef<HTMLButtonElement, LegacyIconButtonProps>(({ title, side = 'bottom', sideOffset = 6, ...props }, ref) => {
    if (title) {
      return (
        <Tooltip.Provider disableHoverableContent>
          <Tooltip.Root delayDuration={300}>
            <Tooltip.Trigger asChild>
              <LegacyIconButtonImpl ref={ref} {...props} />
            </Tooltip.Trigger>
            {title && (
              <Tooltip.Portal>
                <Tooltip.Content side={side} align="center" sideOffset={sideOffset}>
                  {title}
                </Tooltip.Content>
              </Tooltip.Portal>
            )}
          </Tooltip.Root>
        </Tooltip.Provider>
      );
    }

    return <LegacyIconButtonImpl ref={ref} {...props} />;
  }),
);

const LegacyIconButtonImpl = forwardRef<HTMLButtonElement, LegacyIconButtonProps>(
  (
    {
      icon,
      size = 'xl',
      className,
      tabIndex,
      iconClassName,
      disabledClassName,
      disabled = false,
      onClick,
      children,
      title,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={classNames(
          'flex items-center text-bolt-elements-item-contentDefault bg-transparent rounded-md disabled:cursor-not-allowed enabled:hover:text-bolt-elements-item-contentActive enabled:hover:bg-bolt-elements-item-backgroundActive',
          getPaddingForSize(size),
          {
            [classNames('opacity-30', disabledClassName)]: disabled,
          },
          className,
        )}
        disabled={disabled}
        onClick={(event) => {
          if (disabled) {
            return;
          }

          onClick?.(event);
        }}
        title={typeof title === 'string' ? title : undefined}
        tabIndex={tabIndex}
        {...props}
      >
        {children ? children : <div className={classNames(icon, getIconSize(size), iconClassName)}></div>}
      </button>
    );
  },
);

function getIconSize(size: IconSize) {
  if (size === 'xs') {
    return 'text-xs';
  } else if (size === 'sm') {
    return 'text-sm';
  } else if (size === 'md') {
    return 'text-md';
  } else if (size === 'lg') {
    return 'text-lg';
  } else if (size === 'xl') {
    return 'text-xl';
  } else {
    return 'text-2xl';
  }
}

function getPaddingForSize(size: IconSize) {
  if (size === 'xs') {
    return 'p-0.5';
  }

  return 'p-1';
}
